import React, { FC } from 'react';
import styled from 'styled-components';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import { Order } from '../../../common/model/dto/order/order';
import { OrderDataUtils } from '../../../common/utils/services/order-data-utils';
import breakpoints from '../../../design-system/breakpoints';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import OvLabelledField from '../atoms/OvLabelledField';

interface OvOrderDeliveryDatesProps {
  order: Pick<Order, 'shipped_at' | 'estimated_delivery_at' | 'delivered_at'>;
  overdueDays?: number;
}

const DATE_FORMAT = 'DD MMM YYYY';
const TIME_FORMAT = 'HH:mm';

const OvOrderDeliveryDates: FC<OvOrderDeliveryDatesProps> = ({
  order,
  overdueDays,
}) => {
  const { t } = useTranslation();
  const shippedTime = OrderDataUtils.formatDateTime(
    order.shipped_at,
    TIME_FORMAT
  );

  return (
    <Track>
      <Fact $isRecorded={!!order.shipped_at}>
        {order.shipped_at ? <CheckIcon aria-hidden="true" /> : <EmptyDot />}
        <OvLabelledField label={t('orders.delivery.shipped')}>
          {order.shipped_at
            ? OrderDataUtils.formatDateTime(order.shipped_at, DATE_FORMAT)
            : t('orders.delivery.notRecorded')}
          {order.shipped_at && (
            <Secondary>
              {overdueDays === undefined
                ? shippedTime
                : `${shippedTime} · ${t('orders.delivery.daysAgo', {
                    count: overdueDays,
                  })}`}
            </Secondary>
          )}
        </OvLabelledField>
      </Fact>
      <Estimate>
        <OvLabelledField label={t('orders.delivery.estimatedDeliveryDate')}>
          {order.estimated_delivery_at
            ? OrderDataUtils.formatDateTime(
                order.estimated_delivery_at,
                DATE_FORMAT
              )
            : t('orders.delivery.notProvided')}
          {order.estimated_delivery_at && (
            <Secondary>{t('orders.delivery.carrierEstimate')}</Secondary>
          )}
        </OvLabelledField>
      </Estimate>
      <Fact $isRecorded={!!order.delivered_at}>
        {order.delivered_at ? <CheckIcon aria-hidden="true" /> : <EmptyDot />}
        <OvLabelledField label={t('orders.delivery.delivered')}>
          {order.delivered_at
            ? OrderDataUtils.formatDateTime(order.delivered_at, DATE_FORMAT)
            : t('orders.delivery.notRecorded')}
          {order.delivered_at && (
            <Secondary>
              {OrderDataUtils.formatDateTime(order.delivered_at, TIME_FORMAT)}
            </Secondary>
          )}
        </OvLabelledField>
      </Fact>
    </Track>
  );
};

export default OvOrderDeliveryDates;

const Track = styled.dl`
  display: grid;
  grid-template-columns: 1fr 1.15fr 1fr;
  gap: 1.25rem;
  align-items: start;
  margin: 1.125rem 0 0.75rem;

  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const Fact = styled.div<{ $isRecorded: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding-top: 0.625rem;
  border-top: 2px solid
    ${({ $isRecorded }) =>
      $isRecorded ? Colours.OV_BASE : Colours.OV_BORDER_COLOR_V3};

  .MuiSvgIcon-root {
    flex: none;
    width: 1rem;
    height: 1rem;
    margin-top: 0.125rem;
  }
`;

const Estimate = styled.div`
  padding-top: 0.625rem;
  border-top: 2px dashed ${Colours.OV_BORDER_COLOR_V3};
`;

const EmptyDot = styled.span.attrs({ 'aria-hidden': true })`
  flex: none;
  width: 0.75rem;
  height: 0.75rem;
  margin-top: 0.1875rem;
  border: 1px solid ${Colours.OV_CHARCOAL};
  border-radius: ${Variables.borderRadius.CIRCLE};
  box-sizing: border-box;
`;

const Secondary = styled.span`
  display: block;
  color: ${Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.SMALL};
`;
